import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  getOrCreateFulfillmentSettings, updateFulfillmentSettings,
  listZones, createZone, deleteZone,
  listTotes, createTotes, deleteTote,
  listCustomers,
} from "@/lib/admin-config.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Trash2, Boxes, MapPinned, Settings, Users, Search, Layers, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { CustomerConfigDialog } from "@/components/admin/customer-config-dialog";
import { Link } from "@tanstack/react-router";

export const Route = createFileRoute("/_authenticated/app/admin/fulfillment")({ component: FulfillmentAdmin });

const PICK_STRATS = ["discrete", "pick_to_bin", "batch", "zone", "wave"] as const;
const PACK_STRATS = ["discrete", "bulk", "scan_verify"] as const;
type PickStrat = (typeof PICK_STRATS)[number];
type PackStrat = (typeof PACK_STRATS)[number];

const PICK_LABEL: Record<PickStrat, string> = {
  discrete: "Order-by-order",
  pick_to_bin: "Pick to cart",
  batch: "Bulk / batch",
  zone: "Zone",
  wave: "Wave",
};
const PACK_LABEL: Record<PackStrat, string> = {
  discrete: "Order-by-order",
  bulk: "Bulk",
  scan_verify: "Scan verification",
};

function FulfillmentAdmin() {
  return (
    <div className="p-4 sm:p-6">
      <Tabs defaultValue="settings">
        <TabsList>
          <TabsTrigger value="settings">Facility defaults</TabsTrigger>
          <TabsTrigger value="by-customer">By customer</TabsTrigger>
          <TabsTrigger value="zones">Zones</TabsTrigger>
          <TabsTrigger value="totes">Totes</TabsTrigger>
          <TabsTrigger value="waves">Wave policies</TabsTrigger>
        </TabsList>
        <TabsContent value="settings" className="mt-4"><SettingsTab /></TabsContent>
        <TabsContent value="by-customer" className="mt-4"><ByCustomerTab /></TabsContent>
        <TabsContent value="zones" className="mt-4"><ZonesTab /></TabsContent>
        <TabsContent value="totes" className="mt-4"><TotesTab /></TabsContent>
        <TabsContent value="waves" className="mt-4">
          <div className="rounded-lg border bg-card p-6 space-y-3 max-w-2xl">
            <div className="flex items-start gap-3">
              <Layers className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <h2 className="text-base font-semibold">Wave policies</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Wave policies govern <em>when</em> and <em>how much</em> work is released to the
                  picking floor. Picking methods (single, batch, zone, cluster) still determine
                  execution. Policies are optional — without the wave_planning capability, Pick
                  Release behaves like today's existing flow.
                </p>
              </div>
            </div>
            <Button asChild>
              <Link to="/app/admin/wave-policies">
                Configure wave policies <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ByCustomerTab() {
  const qc = useQueryClient();
  const { tenantId } = useScope();
  const fetchFn = useServerFn(listCustomers);
  const [editing, setEditing] = useState<any | null>(null);
  const [search, setSearch] = useState("");

  const { data: customers = [], isLoading } = useQuery({
    queryKey: ["customers"],
    queryFn: () => runServerFn(() => fetchFn(), "Loading customers"),
  });

  const filtered = useMemo(() => {
    const rows = (customers as any[]).filter((c) => !tenantId || c.tenant_id === tenantId);
    const s = search.trim().toLowerCase();
    return s ? rows.filter((c) => c.name.toLowerCase().includes(s)) : rows;
  }, [customers, tenantId, search]);

  const summarize = (c: any) => {
    const pick = (c.enabled_pick_strategies ?? []) as string[];
    const pack = (c.enabled_pack_strategies ?? []) as string[];
    const usesOverrides = pick.length > 0 || pack.length > 0 || c.default_pick_strategy || c.default_pack_strategy;
    return { usesOverrides, pick, pack };
  };

  return (
    <div className="space-y-4">
      <div className="border rounded-lg p-4 bg-card">
        <div className="flex items-start gap-3">
          <Users className="h-5 w-5 mt-0.5 text-muted-foreground" />
          <div className="flex-1 text-sm text-muted-foreground">
            Per-customer overrides take precedence over the facility defaults. Customers without overrides
            inherit the strategies and policies configured under <strong>Facility defaults</strong>.
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search customers…" className="pl-8" />
        </div>
      </div>

      <div className="border rounded-lg bg-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Customer</TableHead>
            <TableHead>Pick strategies</TableHead>
            <TableHead>Pack strategies</TableHead>
            <TableHead>Default carrier</TableHead>
            <TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No customers in this tenant.</TableCell></TableRow>
            ) : filtered.map((c: any) => {
              const { usesOverrides, pick, pack } = summarize(c);
              return (
                <TableRow key={c.id}>
                  <TableCell>
                    <div className="font-medium">{c.name}</div>
                    {!usesOverrides && <div className="text-xs text-muted-foreground">Inherits facility defaults</div>}
                  </TableCell>
                  <TableCell className="text-xs">
                    {pick.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {pick.map((p) => <Badge key={p} variant="secondary">{p}</Badge>)}
                      </div>
                    ) : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-xs">
                    {pack.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {pack.map((p) => <Badge key={p} variant="secondary">{p}</Badge>)}
                      </div>
                    ) : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {c.default_carrier ? `${c.default_carrier}${c.default_service ? ` · ${c.default_service}` : ""}` : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => setEditing(c)}>
                      <Settings className="h-4 w-4 mr-1" /> Configure
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <CustomerConfigDialog
        customer={editing}
        onClose={() => setEditing(null)}
        onSaved={() => qc.invalidateQueries({ queryKey: ["customers"] })}
      />
    </div>
  );
}

function SettingsTab() {
  const qc = useQueryClient();
  const { tenantId, facilityId } = useScope();
  const fetchFn = useServerFn(getOrCreateFulfillmentSettings);
  const updateFn = useServerFn(updateFulfillmentSettings);

  const { data: settings, isLoading } = useQuery({
    queryKey: ["fulfillment-settings", facilityId],
    enabled: !!facilityId && !!tenantId,
    queryFn: () => runServerFn(() => fetchFn({ data: { tenantId: tenantId!, facilityId: facilityId! } }), "Loading settings"),
  });

  const update = async (patch: Record<string, any>) => {
    if (!settings) return;
    try {
      await runServerFn(() => updateFn({ data: { id: settings.id, patch } }), "Updating settings");
      qc.invalidateQueries({ queryKey: ["fulfillment-settings", facilityId] });
    } catch { /* */ }
  };

  if (!facilityId) return <p className="text-sm text-muted-foreground">Select a facility.</p>;
  if (isLoading || !settings) return <Loader2 className="h-5 w-5 animate-spin" />;

  const enabledPick = (settings.enabled_pick_strategies ?? []) as PickStrat[];
  const enabledPack = (settings.enabled_pack_strategies ?? []) as PackStrat[];
  const togglePick = (s: PickStrat, on: boolean) => update({ enabled_pick_strategies: on ? Array.from(new Set([...enabledPick, s])) : enabledPick.filter((x) => x !== s) });
  const togglePack = (s: PackStrat, on: boolean) => update({ enabled_pack_strategies: on ? Array.from(new Set([...enabledPack, s])) : enabledPack.filter((x) => x !== s) });

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="border rounded-lg p-4 bg-card">
        <h3 className="font-semibold mb-3 flex items-center gap-2"><Boxes className="h-4 w-4" />Pick strategies</h3>
        <div className="space-y-2">
          {PICK_STRATS.map((s) => (
            <div key={s} className="flex items-center justify-between p-2 rounded hover:bg-muted/40">
              <Label htmlFor={`pick-${s}`} className="cursor-pointer">{PICK_LABEL[s]}</Label>
              <Switch id={`pick-${s}`} checked={enabledPick.includes(s)} onCheckedChange={(v) => togglePick(s, !!v)} />
            </div>
          ))}
        </div>
        <div className="mt-4 space-y-2">
          <Label>Default</Label>
          <Select value={settings.default_pick_strategy} onValueChange={(v) => update({ default_pick_strategy: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{PICK_STRATS.map((s) => <SelectItem key={s} value={s}>{PICK_LABEL[s]}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="border rounded-lg p-4 bg-card">
        <h3 className="font-semibold mb-3">Pack Strategies</h3>
        <div className="space-y-2">
          {PACK_STRATS.map((s) => (
            <div key={s} className="flex items-center justify-between p-2 rounded hover:bg-muted/40">
              <Label htmlFor={`pack-${s}`} className="cursor-pointer">{PACK_LABEL[s]}</Label>
              <Switch id={`pack-${s}`} checked={enabledPack.includes(s)} onCheckedChange={(v) => togglePack(s, !!v)} />
            </div>
          ))}
        </div>
        <div className="mt-4 space-y-2">
          <Label>Default</Label>
          <Select value={settings.default_pack_strategy} onValueChange={(v) => update({ default_pack_strategy: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{PACK_STRATS.map((s) => <SelectItem key={s} value={s}>{PACK_LABEL[s]}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="border rounded-lg p-4 bg-card lg:col-span-2 space-y-3">
        <h3 className="font-semibold">Policies</h3>
        <div className="flex items-center justify-between">
          <div><Label>Require scan verification on pack</Label><p className="text-xs text-muted-foreground">Forces every pack session to behave as scan-verify.</p></div>
          <Switch checked={settings.require_scan_verify} onCheckedChange={(v) => update({ require_scan_verify: !!v })} />
        </div>
        <div className="flex items-center justify-between">
          <div><Label>Allow partial shipments</Label><p className="text-xs text-muted-foreground">Ship what's packed; leave the remainder open.</p></div>
          <Switch checked={settings.allow_partial_shipments} onCheckedChange={(v) => update({ allow_partial_shipments: !!v })} />
        </div>
        <div className="flex items-center justify-between">
          <div><Label>Allow split shipments</Label><p className="text-xs text-muted-foreground">Split a shipment into multiple parcels with separate labels.</p></div>
          <Switch checked={settings.allow_split_shipments} onCheckedChange={(v) => update({ allow_split_shipments: !!v })} />
        </div>
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <Label>In-common SKU overlap threshold</Label>
            <p className="text-xs text-muted-foreground">Minimum SKU overlap (Jaccard %) for orders to be grouped as “In common”. Default 50%.</p>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={1}
              max={100}
              className="w-24"
              defaultValue={settings.in_common_threshold_pct ?? 50}
              onBlur={(e) => {
                const v = Math.max(1, Math.min(100, Number(e.target.value) || 50));
                if (v !== (settings.in_common_threshold_pct ?? 50)) update({ in_common_threshold_pct: v });
              }}
            />
            <span className="text-sm text-muted-foreground">%</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function ZonesTab() {
  const qc = useQueryClient();
  const { tenantId, facilityId } = useScope();
  const fetchFn = useServerFn(listZones);
  const createFn = useServerFn(createZone);
  const deleteFn = useServerFn(deleteZone);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [seq, setSeq] = useState("100");

  const { data: zones = [] } = useQuery({
    queryKey: ["zones", facilityId],
    enabled: !!facilityId,
    queryFn: () => runServerFn(() => fetchFn({ data: { facilityId: facilityId! } }), "Loading zones"),
  });

  const add = async () => {
    if (!tenantId || !facilityId || !code || !name) return;
    try {
      await runServerFn(() => createFn({ data: {
        tenantId, facilityId,
        code: code.trim().toUpperCase(),
        name: name.trim(),
        pickSequence: Number(seq) || 100,
      } }), "Adding zone");
      setCode(""); setName(""); setSeq("100");
      qc.invalidateQueries({ queryKey: ["zones", facilityId] });
    } catch { /* */ }
  };
  const remove = async (id: string) => {
    if (!confirm("Delete zone?")) return;
    try {
      await runServerFn(() => deleteFn({ data: { id } }), "Deleting zone");
      qc.invalidateQueries({ queryKey: ["zones", facilityId] });
    } catch { /* */ }
  };

  return (
    <div className="space-y-4">
      <div className="border rounded-lg p-4 bg-card">
        <h3 className="font-semibold mb-3 flex items-center gap-2"><MapPinned className="h-4 w-4" />Add zone</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div><Label>Code</Label><Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="A" /></div>
          <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Aisle A" /></div>
          <div><Label>Pick sequence</Label><Input type="number" value={seq} onChange={(e) => setSeq(e.target.value)} /></div>
          <div className="flex items-end"><Button onClick={add}><Plus className="h-4 w-4 mr-1" />Add</Button></div>
        </div>
      </div>
      <div className="border rounded-lg bg-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Sequence</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {zones.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No zones yet.</TableCell></TableRow>
            ) : zones.map((z: any) => (
              <TableRow key={z.id}>
                <TableCell className="font-mono">{z.code}</TableCell>
                <TableCell>{z.name}</TableCell>
                <TableCell>{z.pick_sequence}</TableCell>
                <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => remove(z.id)}><Trash2 className="h-4 w-4" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function TotesTab() {
  const qc = useQueryClient();
  const { tenantId, facilityId } = useScope();
  const fetchFn = useServerFn(listTotes);
  const createFn = useServerFn(createTotes);
  const deleteFn = useServerFn(deleteTote);
  const [code, setCode] = useState("");
  const [bulkPrefix, setBulkPrefix] = useState("TOTE");
  const [bulkCount, setBulkCount] = useState("10");

  const { data: totes = [] } = useQuery({
    queryKey: ["totes", facilityId],
    enabled: !!facilityId,
    queryFn: () => runServerFn(() => fetchFn({ data: { facilityId: facilityId! } }), "Loading totes"),
  });

  const addOne = async () => {
    if (!tenantId || !facilityId || !code) return;
    try {
      await runServerFn(() => createFn({ data: { tenantId, facilityId, codes: [code.trim().toUpperCase()] } }), "Adding tote");
      setCode("");
      qc.invalidateQueries({ queryKey: ["totes", facilityId] });
    } catch { /* */ }
  };
  const bulk = async () => {
    if (!tenantId || !facilityId) return;
    const n = Math.max(1, Math.min(500, Number(bulkCount) || 0));
    const start = (totes.length || 0) + 1;
    const codes = Array.from({ length: n }).map((_, i) => `${bulkPrefix.trim().toUpperCase()}-${String(start + i).padStart(4, "0")}`);
    try {
      const res = await runServerFn(() => createFn({ data: { tenantId, facilityId, codes } }), "Generating totes");
      toast.success(`Created ${res.count} totes`);
      qc.invalidateQueries({ queryKey: ["totes", facilityId] });
    } catch { /* */ }
  };
  const remove = async (id: string) => {
    if (!confirm("Delete tote?")) return;
    try {
      await runServerFn(() => deleteFn({ data: { id } }), "Deleting tote");
      qc.invalidateQueries({ queryKey: ["totes", facilityId] });
    } catch { /* */ }
  };

  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div className="border rounded-lg p-4 bg-card">
          <h3 className="font-semibold mb-3">Add Tote</h3>
          <div className="flex gap-2">
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="TOTE-0001" />
            <Button onClick={addOne}><Plus className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="border rounded-lg p-4 bg-card">
          <h3 className="font-semibold mb-3">Bulk Generate</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            <div><Label>Prefix</Label><Input value={bulkPrefix} onChange={(e) => setBulkPrefix(e.target.value)} /></div>
            <div><Label>Count</Label><Input type="number" value={bulkCount} onChange={(e) => setBulkCount(e.target.value)} /></div>
            <div className="flex items-end"><Button onClick={bulk}>Generate</Button></div>
          </div>
        </div>
      </div>
      <div className="border rounded-lg bg-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Status</TableHead><TableHead>In session</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {totes.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No totes.</TableCell></TableRow>
            ) : totes.map((t: any) => (
              <TableRow key={t.id}>
                <TableCell className="font-mono">{t.code}</TableCell>
                <TableCell><Badge variant="secondary">{t.status}</Badge></TableCell>
                <TableCell className="text-xs text-muted-foreground">{t.current_session_id ? t.current_session_id.slice(0, 8) : "—"}</TableCell>
                <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => remove(t.id)}><Trash2 className="h-4 w-4" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
